import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { untilDestroyed } from '@app/core';
import { Customer, Invoice, Order, PaymentMethodsEnum, Plan, User, PlansPageTopTab, CurrencyOptions, CustomCoupon, InvoiceTypeEnum, GTMEventType, CurrencyEnum } from '@app/models';
import { AlertModalService, LoaderService, SetMissingUserInfoModalService } from '@app/shared';
import { LdatePipe } from '@app/shared/pipes';
import { AppService, EnterpriseService, ErrorHandlerService, GoogleAnalyticsService, NotyService, OrganizationService, PaymentService, UserService } from '@app/services';
import { EditPaymentInfoModalService, EditPaymentInfoModalStep } from './edit-payment-info-modal/edit-payment-info-modal.service';
import { PartnersPlanModalService } from './partners-plan-modal/partners-plan-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { InvoicesComponent } from '@app/pages/invoices/invoices.component';
import { cloneDeep } from 'lodash';

type CustomPlan = Plan & { promo?: Plan };

export const ORGANIZATIONS_BEFORE_THIS_DATE_GET_20_DOCS = new Date('2026-07-03T00:00:00-03:00');

@Component({
  selector: 'app-configurations-plan',
  templateUrl: './configurations-plan.component.html',
  styleUrls: ['../configurations.scss', './configurations-plan.component.scss', '../../../shared/payment-warning/payment-warning.component.scss'],
  standalone: false
})
export class ConfigurationsPlanComponent implements OnInit, OnDestroy {
  plans: Plan[];
  enterprisePlanIds: string[] = ['campsoft'];

  firstInvoice: Invoice | null = null;

  currentUser: User;
  currentPlan: CustomPlan;
  currentOrderPlan: CustomPlan;
  processingPlan: CustomPlan;
  featuredPlan: CustomPlan;
  planInSale: CustomPlan;
  currentOrder: Order;
  currentCustomer: Customer;
  PaymentMethodsEnum = PaymentMethodsEnum;
  currency: CurrencyOptions = 'BRL';
  coupon: CustomCoupon;
  isLoading = false;
  isYearly = false;
  isMobile = false;

  currentTab: PlansPageTopTab = PlansPageTopTab.Plan;
  pageTopTabLoad: { [key in PlansPageTopTab]?: boolean } = {
    [PlansPageTopTab.Plan]: true
  };
  readonly PlansPageTopTab = PlansPageTopTab;

  readonly EditPaymentInfoModalStep = EditPaymentInfoModalStep;
  @ViewChild('invoicesComp') invoicesComp: InvoicesComponent;

  private readonly plansIntervalMap: Record<number, { subject: string; title: string }> = {
    1: { subject: this.translateService.instant('settings.month'), title: this.translateService.instant('settings.monthly') },
    6: { subject: this.translateService.instant('settings.semester'), title: this.translateService.instant('settings.semiAnnual') },
    9: { subject: this.translateService.instant('settings.nineMonths'), title: this.translateService.instant('settings.9Months') },
    12: { subject: this.translateService.instant('settings.year'), title: this.translateService.instant('settings.annual') }
  };

  readonly CurrencyEnum = CurrencyEnum;

  constructor(
    public appService: AppService,
    public translateService: TranslateService,
    public userService: UserService,
    public organizationService: OrganizationService,
    public paymentService: PaymentService,
    public enterpriseService: EnterpriseService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private errorHandlerService: ErrorHandlerService,
    private alertModalService: AlertModalService,
    private notyService: NotyService,
    private loaderService: LoaderService,
    private editPaymentInfoModalService: EditPaymentInfoModalService,
    private setMissingUserInfoModalService: SetMissingUserInfoModalService,
    private ldatePipe: LdatePipe,
    private partnersPlanModalService: PartnersPlanModalService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {}

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    const plans$ = this.paymentService.plans();
    const currentOrder$ = this.paymentService.order();

    this.userService
      .watchCurrentUser({ fetchPolicy: 'network-only' })
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          this.currentUser = user;
          this.currency = this.appService.currencyForCountry(this.currentUser.organization.country);

          if (this.currentUser.organization.parent) {
            this.changeTab(PlansPageTopTab.Credits);
          }

          if (this.currentUser.currentPermissions.change_plan_oz) {
            this.isLoading = true;
            this.loaderService.show();
            forkJoin([plans$, currentOrder$])
              .pipe(
                finalize(() => {
                  this.isLoading = false;
                  this.loaderService.hide();
                })
              )
              .subscribe({
                next: ([plans, currentOrder]) => {
                  this.plans = plans;
                  this.setCurrentOrder(currentOrder);

                  const currentPlan = this.plans.find(plan => plan.id === this.currentUser.subscription.plan_id);
                  this.currentPlan = !this.userService.isSubscriptionFree(this.currentUser) || (this.currentOrder && this.currentOrder?.active && !this.currentOrder?.suspended) ? currentPlan : null;
                  if (this.paymentService.hasSubscriptionInvoicePending(this.currentOrder) && !this.paymentService.isSubscriptionActive(this.currentUser)) {
                    this.processingPlan = this.plans.find(plan => plan.name === this.currentOrder?.plan_name && plan.amount / 100 === this.currentOrder?.price);
                  }
                },
                complete: () => {
                  this.setPromotions(this.currentPlan, this.plans);
                  this.setPlanInSale(this.plans);
                }
              });

            setTimeout(
              () =>
                this.paymentService.currentCustomer().subscribe({
                  next: currentCustomer => (this.currentCustomer = currentCustomer)
                }),
              100
            );
          }
        }
      });

    this.userService.getCurrentUser({ fetchPolicy: 'cache-first' }).subscribe({
      next: () => {
        this.setMissingUserInfoModalService.openIfMissingUserInfo({ ignoreCpf: true, dismissAsError: true, ignoreBirthdate: true }).subscribe({
          error: () => this.router.navigate(['/'])
        });
      }
    });

    this.route.fragment.subscribe({
      next: (fragment: string | null) => {
        if (Object.values(PlansPageTopTab).includes(fragment as PlansPageTopTab)) {
          const tab = fragment as PlansPageTopTab;
          this.changeTab(tab, false);
        }
      }
    });
  }

  get planCurrency(): CurrencyOptions {
    return (this.currentPlan?.currency as CurrencyOptions) || 'BRL';
  }

  changeTab(tab: PlansPageTopTab, updateUrl: boolean = true) {
    this.currentTab = tab;

    if (!this.pageTopTabLoad[tab]) {
      this.pageTopTabLoad[tab] = true;
    }

    if (updateUrl) {
      const url = this.router.createUrlTree([], { relativeTo: this.route, fragment: tab }).toString();
      this.location.go(url);
    }
  }

  onInvoicesLoaded(invoices: Invoice[]) {
    this.firstInvoice = invoices[0] ?? null;
  }

  onAdditionalValidationInvoice() {
    this.invoicesComp?.loadInvoices();
  }

  ngOnDestroy() {}

  isGovernmentUser() {
    return this.currentUser && (this.currentUser.subscription.name || '').toLowerCase() === 'governamental';
  }

  hasGatewaySubscriptionExpired() {
    return this.paymentService.hasGatewaySubscriptionExpired(this.currentOrder);
  }

  isSubscriptionActive() {
    return this.paymentService.isSubscriptionActive(this.currentUser);
  }

  buySelectedPlan(plan: CustomPlan) {
    const editPayment$ = this.editPaymentInfoModalService.open({
      currentCustomer: this.currentCustomer,
      currentOrder: this.currentOrder,
      plan
    });

    const result$ =
      this.currentOrder?.latest_invoice?.status === 'pending' && !this.currentOrder?.suspended
        ? this.alertModalService
            .warning({
              text: this.translateService.instant('settings.alertSwitchPlanPending')
            })
            .pipe(
              switchMap(() => {
                return editPayment$;
              })
            )
        : editPayment$;

    result$.subscribe({
      next: data => {
        this.currentCustomer = data.currentCustomer;
        this.createOrUpdateOrder(plan, data.payableWith, data.cardToken, null, data.couponCode);
      }
    });
  }

  cancelSubscription(isBuyPlan?: boolean): Observable<any> {
    return new Observable(subscriber => {
      if (this.currentOrder) {
        this.paymentService
          .cancelSubscription()
          .pipe(
            finalize(() => {
              if (!isBuyPlan) {
                this.isLoading = false;
                this.loaderService.hide();
                this.googleAnalyticsService.emitGTMEvent(GTMEventType.SubscriptionCancelled, this.currentUser.id);
              }
            })
          )
          .subscribe({
            next: () => {
              if (isBuyPlan) {
                subscriber.next(null);
                subscriber.complete();
                return;
              }
              if (this.currentUser.subscription.plan_id) {
                this.notyService.success(
                  this.translateService.instant('notyService.currentPlanCancelled', {
                    date: this.ldatePipe.transform(this.currentUser.subscription.expires_at, 'longDate')
                  }),
                  10000
                );
              } else {
                this.notyService.success(this.translateService.instant('notyService.currentPlanCancelledImmediately'), 10000);
                this.userService.getCurrentUser({ fetchPolicy: 'network-only' }).subscribe();
              }
              this.router.navigate(['/']);
              subscriber.next(null);
              subscriber.complete();
            },
            error: error => {
              this.errorHandlerService.handle(error);
              subscriber.error(error);
            }
          });
      } else {
        const error = this.translateService.instant('notyService.noActiveSignatureFound');
        this.notyService.error(error);
        subscriber.error(new Error(error));
      }
    });
  }

  triggerCancelSubscription() {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.changePlan')
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        }),
        tap(() => {
          this.isLoading = true;
          this.loaderService.show();
        }),
        switchMap(() => {
          return this.cancelSubscription();
        })
      )
      .subscribe();
  }

  setPlanInSale(allPlans?: CustomPlan[]) {
    this.planInSale = (allPlans || this.plans || []).find(
      plan =>
        ['corporativo_mensal', 'corporativo_anual', 'professional_monthly', 'professional_annual'].includes(plan?.id) && plan?.currency === this.currency && plan?.interval === (this.isYearly ? 12 : 1)
    );
    this.featuredPlan = (allPlans || this.plans || []).find(plan => {
      return plan?.currency === this.currency && ['profissional_carnaval_499'].includes(plan?.id);
    });
  }

  invoicesPublicUrl() {
    return this.currentUser && this.currentUser.organization ? location.origin + '/notas/' + this.currentUser.organization.uuid : '';
  }

  openEditPaymentInfoModal(step?: EditPaymentInfoModalStep) {
    this.editPaymentInfoModalService
      .open({
        currentCustomer: this.currentCustomer,
        currentOrder: this.currentOrder,
        lockStep: step
      })
      .subscribe({
        next: data => {
          if (data?.currentCustomer) this.currentCustomer = data.currentCustomer;
          if (data?.payableWith && step === EditPaymentInfoModalStep.paymentMethod) {
            this.createOrUpdateOrder(null, data.payableWith, data?.cardToken, step);
            return;
          }

          this.notyService.success(this.translateService.instant('notyService.billingInfoUpdated'));
        }
      });
  }

  copyInvoicesLink() {
    this.notyService.success(this.translateService.instant('notyService.invoiceLinkCopied'), 2000);
  }

  getPlansIntervalMap(interval: number) {
    return this.plansIntervalMap[interval] || this.plansIntervalMap[1];
  }

  additionalGroupsCount() {
    return this.organizationService.additionalGroupsCount(this.currentOrder);
  }

  additionalGroupsMonths() {
    return this.organizationService.additionalGroupsMonths(this.currentOrderPlan);
  }

  checkoutTotal() {
    const groupPrice = this.organizationService.additionalGroupPrices[(this.currentOrderPlan?.currency as CurrencyOptions) || this.currency];
    let planPrice = (this.currentOrder?.price || 0) / 100 || this.currentPlan?.amount / 100 || 0;
    if (this.coupon?.discountAmount) planPrice = planPrice - this.coupon.discountAmount / 100;
    return planPrice + this.additionalGroupsCount() * this.additionalGroupsMonths() * groupPrice;
  }

  currencyCode(currency?: string) {
    return this.appService.currencyCodeFor(currency || this.currency);
  }

  isPendingCreditInvoice(invoice: Invoice) {
    const regex = /cr[eé]ditos?|credit/i;
    return invoice && invoice?.status === 'pending' && regex.test(invoice?.description);
  }

  openCalendarGoogle() {
    const url = 'https://calendar.app.google/WcEuNRofJi1qv5nR8';
    window.open(url, '_blank');
  }

  openPartnersPlanModal() {
    this.partnersPlanModalService.open().subscribe();
  }

  isSameCurrencyForCurrentPLan(): boolean {
    return this.currency === this.currentPlan?.currency;
  }

  getPlanName() {
    if (this.currentPlan && this.hasEnterprisePlanIds()) {
      return this.currentPlan.name;
    }
    return 'settings.free';
  }

  getPlanDocumentLimit() {
    if (this.currentPlan && this.hasEnterprisePlanIds()) {
      return 30;
    }

    return this.currentUser?.organization?.created_at && new Date(this.currentUser.organization.created_at) < ORGANIZATIONS_BEFORE_THIS_DATE_GET_20_DOCS ? 20 : 10;
  }

  hasEnterprisePlanIds() {
    return this.currentPlan && this.enterprisePlanIds.includes(this.currentPlan.id);
  }

  private createOrUpdateOrder(plan?: CustomPlan, payableWith?: PaymentMethodsEnum, cardToken?: string, lockStep?: EditPaymentInfoModalStep, coupon?: string) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.loaderService.show();

      const isUpdate = this.currentOrder && !this.currentOrder?.suspended && (this.currentOrder?.latest_invoice?.status !== 'pending' || lockStep);
      const order$ = isUpdate
        ? this.paymentService.updateSubscription({
            ...(plan ? { plan: plan.id } : {}),
            ...((payableWith && payableWith !== this.currentOrder?.payment_method) || cardToken ? { payment_method: payableWith } : {}),
            ...(cardToken ? { card_token: cardToken } : {}),
            ...(coupon ? { coupon } : {})
          })
        : this.paymentService.createSubscription({
            plan: plan.id,
            payment_method: payableWith,
            ...(cardToken ? { card_token: cardToken } : {}),
            ...(coupon ? { coupon } : {})
          });

      const cancel$ = this.currentOrder?.latest_invoice?.status === 'pending' && !lockStep ? this.cancelSubscription(true) : of(null);

      cancel$
        .pipe(
          switchMap(() => order$),
          finalize(() => {
            const billing_cycle = plan.interval === 12 ? 'annual' : plan.interval === 9 ? 'carnival' : 'monthly';
            this.isLoading = false;
            this.loaderService.hide();
            this.googleAnalyticsService.emitGTMEvent(GTMEventType.SubscriptionStarted, this.currentUser.id, { plan_type: 'paid', billing_cycle });
          })
        )
        .subscribe({
          next: order => {
            this.processingPlan = plan;
            this.setCurrentOrder(order);
            this.invoicesComp?.loadInvoices();

            if (isUpdate) {
              this.notyService.success(this.translateService.instant('notyService.billingInfoUpdated'));
              return;
            }
            if (payableWith === PaymentMethodsEnum.CreditCard) {
              this.notyService.success(this.translateService.instant('notyService.orderRegisteredWaitingApproval'), 15000);
            } else {
              this.notyService.success(this.translateService.instant('notyService.orderRegisteredWaitingPayment'), 15000);
            }
          },
          error: error => this.errorHandlerService.handle(error)
        });
    }
  }

  private setPromotions(currentPlan: CustomPlan, allPlans: CustomPlan[]) {
    const promotions: Record<string, string> = {};
    if (this.route.snapshot.queryParams.promo) {
      this.route.snapshot.queryParams.promo.split(',').forEach((promotion: string) => (promotions[promotion.split('-')[0]] = promotion.split('-')[1]));
    }

    if (!currentPlan || !promotions[currentPlan.id] || !promotions[currentPlan.stripe_id] || !promotions[currentPlan.iugu_id]) {
      const plans = cloneDeep(allPlans);
      for (const key in promotions) {
        const planBefore = plans.find(plan => plan.id === key || plan.stripe_id === key || plan.iugu_id === key);
        const planAfter = plans.find(plan => plan.id === promotions[key] || plan.stripe_id === promotions[key] || plan.iugu_id === promotions[key]);
        if (planBefore && planAfter) {
          planBefore.promo = planAfter;
          planAfter.active = true;
        }
      }
      this.plans = plans;
    }
  }

  private setCurrentOrder(order: Order) {
    this.currentOrder = order;
    this.currentOrderPlan = this.currentOrder ? this.plans.find(plan => plan.id === this.currentOrder?.plan_id) : this.currentPlan;
    let discountAmount = order?.coupon?.amount_off != null ? order?.coupon?.amount_off : order?.coupon?.percent_off != null ? this.currentOrderPlan.amount * (order?.coupon?.percent_off / 100) : 0;
    // Ensure the discount does not exceed plan amount
    if (discountAmount > this.currentOrderPlan?.amount) {
      discountAmount = this.currentOrderPlan?.amount;
    }
    this.coupon = { ...order?.coupon, discountAmount };
  }

  protected readonly InvoiceTypeEnum = InvoiceTypeEnum;
}
